import { useEffect } from 'react';
import { Providers } from './providers';
import { AppRouter } from './router';
import { useAuth } from '@/pages/auth';
import { init as analyticsInit } from '@/lib/analytics';

/**
 * Schedule analytics SDK load AFTER the browser is idle. Mixpanel's SDK is
 * ~125 kB gzipped — pulling it in on the App's mount useEffect pushes a
 * non-critical chunk into the LCP / first-interactive window. Deferring
 * it to `requestIdleCallback` (with a `setTimeout` fallback for Safari)
 * lets the first-paint network budget go to the entry bundle, fonts, and
 * LCP image instead. Events fired BEFORE the SDK loads are queued by the
 * facade's `Pending` queue and drained on init — see `analytics.ts`.
 */
function scheduleIdle(cb: () => void, fallbackMs = 1500): void {
  if (typeof window === 'undefined') return;
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(cb, { timeout: 3000 });
  } else {
    window.setTimeout(cb, fallbackMs);
  }
}

export function App() {
  // Restore session on boot (no-op if no persisted token).
  // Mixpanel `init()` is deferred to `requestIdleCallback` — see
  // `scheduleIdle` above for the rationale. Identity is re-applied via
  // `analytics.identify` calls inside the auth-store mutations once the
  // SDK lands; events that fired in the gap are buffered by the facade.
  useEffect(() => {
    void useAuth.getState().init();
    scheduleIdle(() => {
      void analyticsInit();
    });
  }, []);

  return (
    <Providers>
      <AppRouter />
    </Providers>
  );
}
